// Enhanced Google Analytics 4 tracking with detailed click coordinates and session data

import {
  captureSessionAcquisition,
  claimAnalyticsOnce,
  classifyPageType,
  createOutboundClickId,
  getAnalyticsPageViewId,
  getLastCompatibilityDecision,
  getSessionAcquisitionFields,
  hasRecordedEntryPageView,
  markAnalyticsPageView,
  markEntryPageViewRecorded,
  rememberCompatibilityDecision,
} from './sessionAttribution';

export {
  captureSessionAcquisition,
  classifyPageType,
  createOutboundClickId,
  getInboundUtmContext,
  getSessionAcquisition,
  getSessionAcquisitionFields,
  markAnalyticsPageView,
  resetAttributionStateForTests,
} from './sessionAttribution';

export const DEAL_CLICK_EVENT = 'deal_click';
export const CANONICAL_AFFILIATE_CLICK_EVENT = DEAL_CLICK_EVENT;
export const COMPATIBILITY_CHECKED_EVENT = 'compatibility_checked';
export const EARLY_ACCESS_SIGNUP_EVENT = 'early_access_signup';
export const EARLY_ACCESS_CTA_CLICK_EVENT = 'early_access_cta_click';
export const COMPARISON_PRICE_JUMP_EVENT = 'comparison_price_jump';
export const RETAILER_OPTION_IMPRESSION_EVENT = 'retailer_option_impression';
export const COMPARISON_STICKY_PRICE_IMPRESSION_EVENT = 'comparison_sticky_price_impression';
export const COMPARISON_STICKY_PRICE_CLICK_EVENT = 'comparison_sticky_price_click';
export const NEWSLETTER_OPT_IN_EVENT = 'newsletter_opt_in';
export const ENTRY_PAGE_VIEW_EVENT = 'entry_page_view';
export const PAGE_VIEW_DETAILED_EVENT = 'page_view_detailed';
export const PRICE_SECTION_VIEW_EVENT = 'price_section_view';
export const PRIMARY_CTA_CLICK_EVENT = 'primary_cta_click';

export const SHARE_ASSET_DOWNLOAD_EVENT = 'share_asset_download';

export const AFFILIATE_PAGE_SOURCE = {
  PRODUCT_COMPARISON: 'product_comparison',
  PRODUCT_PDP: 'product_pdp',
  COMPATIBILITY_PAGE: 'compatibility_page',
  UPGRADE_COMPARISON: 'upgrade_comparison',
  HOMEPAGE: 'homepage',
  SEASONAL_LAUNCH: 'seasonal_launch',
};

class EnhancedAnalytics {
  constructor() {
    this.sessionId = null;
    this.pageStartTime = null;
    this.clickSequence = 0;
    this.scrollDepth = 0;
    this.isInitialized = false;
    this.init();
  }

  init() {
    // Generate unique session ID
    this.sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    this.pageStartTime = Date.now();
    
    // Check if gtag is available
    if (typeof window !== 'undefined' && window.gtag) {
      this.isInitialized = true;
      console.log('Enhanced Analytics initialized with session:', this.sessionId);
      
      // Set up event listeners. RouteTracker owns page_view_detailed.
      this.setupEventListeners();
    } else {
      console.warn('Google Analytics not available');
    }
  }

  setupEventListeners() {
    // Track all clicks with detailed information
    document.addEventListener('click', (event) => {
      this.trackDetailedClick(event);
    });

    // Track scroll depth
    let maxScrollDepth = 0;
    window.addEventListener('scroll', () => {
      const scrolled = (window.scrollY + window.innerHeight) / document.documentElement.scrollHeight * 100;
      const roundedScroll = Math.round(scrolled / 10) * 10; // Round to nearest 10%
      
      if (roundedScroll > maxScrollDepth) {
        maxScrollDepth = roundedScroll;
        this.scrollDepth = Math.min(roundedScroll, 100);
        
        this.trackEvent('scroll_depth', {
          session_id: this.sessionId,
          scroll_depth: this.scrollDepth,
          page_path: window.location.pathname,
          time_on_page: Date.now() - this.pageStartTime
        });
      }
    });



    // Track window blur/focus
    window.addEventListener('blur', () => {
      this.trackEvent('window_blur', {
        session_id: this.sessionId,
        page_path: window.location.pathname,
        time_on_page: Date.now() - this.pageStartTime
      });
    });

    window.addEventListener('focus', () => {
      this.trackEvent('window_focus', {
        session_id: this.sessionId,
        page_path: window.location.pathname,
        time_on_page: Date.now() - this.pageStartTime
      });
    });
  }

  trackDetailedClick(event) {
    if (!this.isInitialized) return;
    
    this.clickSequence++;
    const element = event.target;
    
    // Get detailed element information
    const elementData = {
      session_id: this.sessionId,
      click_sequence: this.clickSequence,
      element_type: element.tagName,
      element_id: element.id || '',
      element_class: element.className || '',
      element_text: element.innerText?.substring(0, 100) || '',
      element_html: element.outerHTML?.substring(0, 200) || '',
      page_x: event.pageX,
      page_y: event.pageY,
      screen_x: event.screenX,
      screen_y: event.screenY,
      page_path: window.location.pathname,
      scroll_position: window.scrollY,
      time_on_page: Date.now() - this.pageStartTime,
      timestamp: new Date().toISOString()
    };

    // Track specific element types with custom events
    if (element.tagName === 'BUTTON') {
      this.trackEvent('button_click', {
        ...elementData,
        button_text: element.innerText?.substring(0, 50) || '',
        button_type: element.type || 'button'
      });
    } else if (element.tagName === 'A') {
      this.trackEvent('link_click', {
        ...elementData,
        link_url: element.href || '',
        link_text: element.innerText?.substring(0, 50) || '',
        is_external: element.href ? !element.href.includes(window.location.hostname) : false
      });
    } else {
      this.trackEvent('element_click', elementData);
    }

    // Also track as general click event for GA4's automatic reporting
    this.trackEvent('click', {
      session_id: this.sessionId,
      click_coordinates: `${event.pageX},${event.pageY}`,
      element_type: element.tagName,
      page_path: window.location.pathname
    });
  }

  trackPageView(pagePath = null, pageTitle = null) {
    try {
      if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
        return;
      }

      this.isInitialized = true;
      const currentPath = pagePath || window.location.pathname;
      const currentTitle = pageTitle || document.title;
      markAnalyticsPageView(currentPath);
      const onceKey = `page_view_detailed:${getAnalyticsPageViewId()}:${currentPath}`;
      if (!claimAnalyticsOnce(onceKey)) return;

      // Reset page-specific tracking
      this.pageStartTime = Date.now();
      this.clickSequence = 0;
      this.scrollDepth = 0;

      // Standard page_view ownership (verified on production collect hits):
      // index.html config sends the initial automatic page_view. A same-load
      // follow-up config does not emit a second page_view. Later SPA configs
      // emit the one standard page_view for the new route. Do not also send
      // gtag('event', 'page_view') because that would duplicate if both fire.
      window.gtag('config', 'G-KXPPK050SC', {
        page_path: currentPath,
        page_title: currentTitle,
        custom_map: {
          'custom_parameter_1': 'session_id'
        }
      });

      this.trackEvent(PAGE_VIEW_DETAILED_EVENT, {
        session_id: this.sessionId,
        page_path: currentPath,
        page_title: currentTitle,
        referrer: document.referrer,
        user_agent: navigator.userAgent,
        screen_resolution: `${window.screen.width}x${window.screen.height}`,
        window_size: `${window.innerWidth}x${window.innerHeight}`,
        timestamp: new Date().toISOString()
      });
    } catch {
      // Analytics must never throw into routing.
    }
  }

  trackEvent(eventName, parameters = {}) {
    try {
      if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
        return;
      }

      this.isInitialized = true;

      // Ensure all parameters are strings or numbers for GA4
      const cleanParams = {};
      Object.keys(parameters).forEach(key => {
        const value = parameters[key];
        if (typeof value === 'string' || typeof value === 'number') {
          cleanParams[key] = value;
        } else if (value !== null && value !== undefined) {
          cleanParams[key] = String(value);
        }
      });

      if (eventName === DEAL_CLICK_EVENT && cleanParams.transport_type == null) {
        cleanParams.transport_type = 'beacon';
      }

      window.gtag('event', eventName, cleanParams);
      console.log(`GA4 Event: ${eventName}`, cleanParams);
    } catch {
      // Analytics must never throw into UI click handlers.
    }
  }

  // Fish Finder specific tracking methods
  trackProductView(productId, productName, category, price = null) {
    this.trackEvent('view_item', {
      session_id: this.sessionId,
      item_id: productId,
      item_name: productName,
      item_category: category,
      value: price,
      currency: 'USD'
    });
  }

  trackDealClick(dealName, retailer, originalPrice, salePrice, dealUrl, context) {
    try {
      const params = {
        session_id: this.sessionId,
        deal_name: dealName,
        retailer: retailer,
        deal_url: dealUrl,
        click_id:
          context && typeof context === 'object' && context.click_id
            ? context.click_id
            : createOutboundClickId(),
      };

      const originalIsNumber = typeof originalPrice === 'number' && Number.isFinite(originalPrice);
      const saleIsNumber = typeof salePrice === 'number' && Number.isFinite(salePrice);
      if (originalIsNumber && saleIsNumber) {
        const savings = originalPrice - salePrice;
        params.original_price = originalPrice;
        params.sale_price = salePrice;
        params.displayed_price = salePrice;
        params.savings_amount = savings;
        params.savings_percent = Math.round((savings / originalPrice) * 100);
      } else if (saleIsNumber) {
        params.sale_price = salePrice;
        params.displayed_price = salePrice;
      }

      const acquisition = getSessionAcquisitionFields();
      Object.assign(params, acquisition);

      if (context && typeof context === 'object') {
        if (context.page_source) params.page_source = context.page_source;
        if (context.page_type) params.page_type = context.page_type;
        if (context.page_path) params.page_path = context.page_path;
        if (context.product_slug) params.product_slug = context.product_slug;
        if (context.product) params.product = context.product;
        if (context.placement) params.placement = context.placement;
        if (context.freshness_state) params.freshness_state = context.freshness_state;
        if (context.comparison_id) params.comparison_id = context.comparison_id;
        if (context.compatibility_id) params.compatibility_id = context.compatibility_id;
        if (context.compatibility_display_slug) {
          params.compatibility_display_slug = context.compatibility_display_slug;
        }
        if (context.compatibility_livescope_slug) {
          params.compatibility_livescope_slug = context.compatibility_livescope_slug;
        }
        if (context.compatibility_result) {
          params.compatibility_result = context.compatibility_result;
        }
        if (context.last_compatibility_id) {
          params.last_compatibility_id = context.last_compatibility_id;
        }
        if (context.last_compatibility_result) {
          params.last_compatibility_result = context.last_compatibility_result;
        }
        if (context.affiliate_url) params.affiliate_url = context.affiliate_url;
        if (context.product_name) params.product_name = context.product_name;
        if (context.brand) params.brand = context.brand;
        if (context.launch_season) params.launch_season = context.launch_season;
        if (context.category) params.category = context.category;
        if (context.link_type) params.link_type = context.link_type;
        if (context.product_scope) params.product_scope = context.product_scope;
        if (context.module) params.module = context.module;
        if (context.cta_text) params.cta_text = context.cta_text;
        if (context.offer_id) params.offer_id = context.offer_id;
        if (context.utm_source) params.utm_source = context.utm_source;
        if (context.utm_medium) params.utm_medium = context.utm_medium;
        if (context.utm_campaign) params.utm_campaign = context.utm_campaign;
        if (context.utm_content) params.utm_content = context.utm_content;
        if (context.utm_term) params.utm_term = context.utm_term;
        if (
          typeof context.displayed_price === 'number' &&
          Number.isFinite(context.displayed_price) &&
          context.displayed_price > 0
        ) {
          params.displayed_price = context.displayed_price;
        }
      }

      this.trackEvent(DEAL_CLICK_EVENT, params);
    } catch {
      // Best-effort only; outbound affiliate navigation must continue.
    }
  }

  trackBundleBuilderStep(step, componentType, componentName) {
    this.trackEvent('bundle_builder_step', {
      session_id: this.sessionId,
      builder_step: step,
      component_type: componentType,
      component_name: componentName,
      step_number: step
    });
  }

  trackSearch(searchTerm, resultsCount) {
    this.trackEvent('search', {
      session_id: this.sessionId,
      search_term: searchTerm,
      results_count: resultsCount
    });
  }

  trackPriceComparison(productName, lowestPrice, highestPrice, retailerCount) {
    this.trackEvent('price_comparison_view', {
      session_id: this.sessionId,
      product_name: productName,
      lowest_price: lowestPrice,
      highest_price: highestPrice,
      retailer_count: retailerCount,
      price_range: highestPrice - lowestPrice
    });
  }

  // Get session analytics summary (for debugging)
  getSessionSummary() {
    return {
      sessionId: this.sessionId,
      timeOnPage: Date.now() - this.pageStartTime,
      clickSequence: this.clickSequence,
      scrollDepth: this.scrollDepth,
      currentPage: window.location.pathname
    };
  }
}

// Create global instance
const enhancedAnalytics = new EnhancedAnalytics();

export function trackPricedAffiliateDealClick({
  retailer,
  affiliateUrl,
  salePrice,
  pageSource,
  productSlug,
  product,
  comparisonId,
  compatibilityId,
  compatibilityDisplaySlug,
  compatibilityLivescopeSlug,
  compatibilityResult,
  offerId,
  search,
  placement,
  freshnessState,
  pagePath,
  pageType,
} = {}) {
  try {
    const priceOk =
      typeof salePrice === 'number' && Number.isFinite(salePrice) && salePrice > 0;
    if (!priceOk || !affiliateUrl || !retailer || !pageSource || !productSlug) {
      return;
    }

    captureSessionAcquisition({ search });
    const resolvedPath =
      pagePath ||
      (typeof window !== 'undefined' && window.location
        ? window.location.pathname
        : '');
    const resolvedType = pageType || classifyPageType(resolvedPath, pageSource);
    const context = {
      click_id: createOutboundClickId(),
      page_source: pageSource,
      product_slug: productSlug,
      retailer,
      affiliate_url: affiliateUrl,
      displayed_price: salePrice,
      ...getSessionAcquisitionFields(),
    };
    if (resolvedPath) context.page_path = resolvedPath;
    if (resolvedType) context.page_type = resolvedType;
    if (product) context.product = product;
    if (placement) context.placement = placement;
    if (freshnessState) context.freshness_state = freshnessState;
    if (comparisonId) context.comparison_id = comparisonId;
    if (compatibilityId) context.compatibility_id = compatibilityId;
    if (compatibilityDisplaySlug) {
      context.compatibility_display_slug = compatibilityDisplaySlug;
    }
    if (compatibilityLivescopeSlug) {
      context.compatibility_livescope_slug = compatibilityLivescopeSlug;
    }
    if (compatibilityResult) context.compatibility_result = compatibilityResult;
    if (!compatibilityId) {
      const lastCompatibility = getLastCompatibilityDecision();
      if (lastCompatibility?.compatibility_id) {
        context.last_compatibility_id = lastCompatibility.compatibility_id;
      }
      if (lastCompatibility?.compatibility_result) {
        context.last_compatibility_result = lastCompatibility.compatibility_result;
      }
    }
    if (offerId) context.offer_id = String(offerId);

    enhancedAnalytics.trackDealClick(
      productSlug,
      retailer,
      undefined,
      salePrice,
      affiliateUrl,
      context
    );
  } catch {
    // Best-effort only; outbound affiliate navigation must continue.
  }
}

export function trackCompatibilityChecked({
  displaySlug,
  livescopeSlug,
  result,
  verificationState,
  addCount,
  replaceCount,
  notUsedCount,
  compatibilityId,
} = {}) {
  try {
    if (!displaySlug || !livescopeSlug || !result) return;
    const onceKey = `compatibility_checked:${getAnalyticsPageViewId()}:${displaySlug}:${livescopeSlug}:${result}`;
    if (!claimAnalyticsOnce(onceKey)) return;
    rememberCompatibilityDecision({
      displaySlug,
      livescopeSlug,
      result,
      verificationState,
      compatibilityId,
    });
    const payload = {
      display_slug: displaySlug,
      livescope_slug: livescopeSlug,
      result,
      verification_state: verificationState || '',
      add_count: Number.isFinite(addCount) ? addCount : 0,
      replace_count: Number.isFinite(replaceCount) ? replaceCount : 0,
      not_used_count: Number.isFinite(notUsedCount) ? notUsedCount : 0,
    };
    if (compatibilityId) payload.compatibility_id = compatibilityId;
    enhancedAnalytics.trackEvent(COMPATIBILITY_CHECKED_EVENT, payload);
  } catch {
    // Analytics must never throw into page render.
  }
}

function omitEmailFields(parameters = {}) {
  const clean = { ...parameters };
  delete clean.email;
  delete clean.user_email;
  delete clean.email_address;
  return clean;
}

export function trackEarlyAccessSignup(parameters = {}) {
  try {
    enhancedAnalytics.trackEvent(EARLY_ACCESS_SIGNUP_EVENT, omitEmailFields(parameters));
  } catch {
    // Analytics must never throw into form handlers.
  }
}

export function trackEarlyAccessCtaClick(parameters = {}) {
  try {
    const { page, placement } = parameters;
    enhancedAnalytics.trackEvent(
      EARLY_ACCESS_CTA_CLICK_EVENT,
      omitEmailFields({ page, placement })
    );
  } catch {
    // Analytics must never throw into UI click handlers.
  }
}

export function trackComparisonPriceJump({ pageSource, comparisonId, placement } = {}) {
  try {
    if (!pageSource || !comparisonId || !placement) return;
    enhancedAnalytics.trackEvent(COMPARISON_PRICE_JUMP_EVENT, {
      page_source: pageSource,
      comparison_id: comparisonId,
      placement,
    });
  } catch {
    // Analytics must never throw into UI click handlers.
  }
}

export function trackRetailerOptionImpression({
  pageSource,
  comparisonId,
  productSlug,
  product,
  retailer,
  position,
  price,
  freshnessState,
  isLowestPrice,
  isTiedLowest,
  placement,
} = {}) {
  try {
    if (!pageSource || !retailer || !placement) return;
    if (!Number.isFinite(position) || position < 1) return;
    const payload = {
      page_source: pageSource,
      retailer,
      position,
      freshness_state: freshnessState || '',
      is_lowest_price: Boolean(isLowestPrice),
      is_tied_lowest: Boolean(isTiedLowest),
      placement,
    };
    if (comparisonId) payload.comparison_id = comparisonId;
    if (productSlug) payload.product_slug = productSlug;
    if (product) payload.product = product;
    if (typeof price === 'number' && Number.isFinite(price) && price > 0) {
      payload.price = price;
    }
    const onceKey = [
      'retailer_option_impression',
      getAnalyticsPageViewId(),
      pageSource,
      comparisonId || '',
      productSlug || '',
      retailer,
      position,
      placement,
    ].join(':');
    if (!claimAnalyticsOnce(onceKey)) return;
    enhancedAnalytics.trackEvent(RETAILER_OPTION_IMPRESSION_EVENT, payload);
  } catch {
    // Analytics must never throw into render or observers.
  }
}

function stickyPriceAnalyticsPayload({
  pageSource,
  comparisonId,
  product,
  price,
  currentLowestRetailerCount,
  currentRetailerCount,
  hasCurrentVerifiedPrice,
  placement,
} = {}) {
  const payload = {
    page_source: pageSource,
    product,
    placement: placement || 'sticky_price_bar',
    current_lowest_retailer_count: currentLowestRetailerCount,
    current_retailer_count: currentRetailerCount,
    has_current_verified_price: Boolean(hasCurrentVerifiedPrice),
  };
  if (comparisonId) payload.comparison_id = comparisonId;
  if (typeof price === 'number' && Number.isFinite(price) && price > 0) {
    payload.price = price;
  }
  return payload;
}

export function trackComparisonStickyPriceImpression(parameters = {}) {
  try {
    if (!parameters.pageSource || !parameters.product) return;
    const onceKey = [
      'comparison_sticky_price_impression',
      getAnalyticsPageViewId(),
      parameters.pageSource,
      parameters.comparisonId || '',
      parameters.product,
      parameters.placement || 'sticky_price_bar',
    ].join(':');
    if (!claimAnalyticsOnce(onceKey)) return;
    enhancedAnalytics.trackEvent(
      COMPARISON_STICKY_PRICE_IMPRESSION_EVENT,
      stickyPriceAnalyticsPayload(parameters)
    );
  } catch {
    // Analytics must never throw into render or observers.
  }
}

export function trackComparisonStickyPriceClick(parameters = {}) {
  try {
    if (!parameters.pageSource || !parameters.product) return;
    enhancedAnalytics.trackEvent(
      COMPARISON_STICKY_PRICE_CLICK_EVENT,
      stickyPriceAnalyticsPayload(parameters)
    );
  } catch {
    // Analytics must never throw into UI click handlers.
  }
}

export function trackNewsletterOptIn(parameters = {}) {
  try {
    enhancedAnalytics.trackEvent(NEWSLETTER_OPT_IN_EVENT, omitEmailFields(parameters));
  } catch {
    // Analytics must never throw into form handlers.
  }
}

export function trackEntryPageView({ path, search } = {}) {
  try {
    if (hasRecordedEntryPageView()) return;
    const acquisition = captureSessionAcquisition({ path, search });
    markEntryPageViewRecorded();
    const pagePath = path || acquisition?.landing_path || '';
    const pageType = classifyPageType(pagePath);
    const payload = {
      page_path: pagePath || '/',
      traffic_source: acquisition?.traffic_source || 'direct',
      ...getSessionAcquisitionFields(),
    };
    if (pageType) payload.page_type = pageType;
    enhancedAnalytics.trackEvent(ENTRY_PAGE_VIEW_EVENT, payload);
  } catch {
    // Analytics must never throw into routing.
  }
}

export function trackPriceSectionView({
  pageSource,
  pagePath,
  productSlug,
  product,
  comparisonId,
  freshnessState,
  retailerCount,
  hasCurrentVerifiedPrice,
  placement,
} = {}) {
  try {
    if (!pageSource) return;
    const resolvedPath =
      pagePath ||
      (typeof window !== 'undefined' && window.location
        ? window.location.pathname
        : '');
    const onceKey = [
      'price_section_view',
      getAnalyticsPageViewId(),
      pageSource,
      resolvedPath,
      comparisonId || '',
      productSlug || '',
      placement || 'current_prices',
    ].join(':');
    if (!claimAnalyticsOnce(onceKey)) return;
    const payload = {
      page_source: pageSource,
      retailer_count: Number.isFinite(retailerCount) ? retailerCount : 0,
      has_current_verified_price: Boolean(hasCurrentVerifiedPrice),
    };
    if (resolvedPath) payload.page_path = resolvedPath;
    const pageType = classifyPageType(resolvedPath, pageSource);
    if (pageType) payload.page_type = pageType;
    if (productSlug) payload.product_slug = productSlug;
    if (product) payload.product = product;
    if (comparisonId) payload.comparison_id = comparisonId;
    if (freshnessState) payload.freshness_state = freshnessState;
    if (placement) payload.placement = placement;
    enhancedAnalytics.trackEvent(PRICE_SECTION_VIEW_EVENT, payload);
  } catch {
    // Analytics must never throw into render or observers.
  }
}

export function trackAffiliateOutboundClick({
  productName,
  brand,
  launchSeason,
  category,
  affiliateUrl,
  linkType,
  productScope,
  placement,
  pagePath,
  module,
  ctaText,
  pageSource,
  pageType,
} = {}) {
  try {
    if (!affiliateUrl || !productName) return;
    const resolvedPath =
      pagePath ||
      (typeof window !== 'undefined' && window.location
        ? window.location.pathname
        : '');
    const resolvedSource = pageSource || AFFILIATE_PAGE_SOURCE.SEASONAL_LAUNCH;
    const resolvedType = pageType || classifyPageType(resolvedPath, resolvedSource);
    const context = {
      click_id: createOutboundClickId(),
      page_source: resolvedSource,
      affiliate_url: affiliateUrl,
      product_name: productName,
      ...getSessionAcquisitionFields(),
    };
    if (resolvedPath) context.page_path = resolvedPath;
    if (resolvedType) context.page_type = resolvedType;
    if (brand) context.brand = brand;
    if (launchSeason) context.launch_season = launchSeason;
    if (category) context.category = category;
    if (linkType) context.link_type = linkType;
    if (productScope) context.product_scope = productScope;
    if (placement) context.placement = placement;
    if (module) context.module = module;
    if (ctaText) context.cta_text = ctaText;
    enhancedAnalytics.trackDealClick(
      productName,
      brand || 'Retailer',
      undefined,
      undefined,
      affiliateUrl,
      context
    );
  } catch {
    // Best-effort only; outbound affiliate navigation must continue.
  }
}

export function trackShareAssetDownload({
  pageType,
  brand,
  launchSeason,
  shortUrl,
  assetType,
  pagePath,
} = {}) {
  try {
    const resolvedPath =
      pagePath ||
      (typeof window !== 'undefined' && window.location
        ? window.location.pathname
        : '');
    const payload = {
      asset_type: assetType || 'qr_graphic',
      ...getSessionAcquisitionFields(),
    };
    if (pageType) payload.page_type = pageType;
    if (brand) payload.brand = brand;
    if (launchSeason) payload.launch_season = launchSeason;
    if (shortUrl) payload.short_url = shortUrl;
    if (resolvedPath) payload.page_path = resolvedPath;
    enhancedAnalytics.trackEvent(SHARE_ASSET_DOWNLOAD_EVENT, payload);
  } catch {
    // Analytics must never throw into UI click handlers.
  }
}

export function trackPrimaryCtaClick({
  pageSource,
  placement,
  cta,
  comparisonId,
  pagePath,
  productSlug,
  destination,
} = {}) {
  try {
    if (!pageSource || !placement || !cta) return;
    const resolvedPath =
      pagePath ||
      (typeof window !== 'undefined' && window.location
        ? window.location.pathname
        : '');
    const payload = {
      page_source: pageSource,
      placement,
      cta,
      ...getSessionAcquisitionFields(),
    };
    if (resolvedPath) payload.page_path = resolvedPath;
    if (comparisonId) payload.comparison_id = comparisonId;
    if (productSlug) payload.product_slug = productSlug;
    if (destination) payload.destination = destination;
    enhancedAnalytics.trackEvent(PRIMARY_CTA_CLICK_EVENT, payload);
  } catch {
    // Analytics must never throw into UI click handlers.
  }
}

export default enhancedAnalytics; 